import { z } from 'zod';
// ---------- Enums ----------
export const InvoiceStatusSchema = z.enum(['BORRADOR', 'EMITIDA', 'PARCIAL', 'COBRADA', 'ANULADA']);
export const InvoiceStatusLabel = {
    BORRADOR: 'Borrador',
    EMITIDA: 'Emitida',
    PARCIAL: 'Cobrada parcial',
    COBRADA: 'Cobrada',
    ANULADA: 'Anulada',
};
export const InvoiceKindSchema = z.enum(['ORDINARIA', 'RECTIFICATIVA', 'PROFORMA']);
export const InvoiceKindLabel = {
    ORDINARIA: 'Ordinaria',
    RECTIFICATIVA: 'Rectificativa',
    PROFORMA: 'Proforma',
};
export const PaymentMethodSchema = z.enum([
    'TRANSFERENCIA',
    'EFECTIVO',
    'TARJETA',
    'CHEQUE',
    'DOMICILIACION',
    'COMPENSACION',
    'OTRO',
]);
export const PaymentMethodLabel = {
    TRANSFERENCIA: 'Transferencia',
    EFECTIVO: 'Efectivo',
    TARJETA: 'Tarjeta',
    CHEQUE: 'Cheque',
    DOMICILIACION: 'Domiciliación',
    COMPENSACION: 'Compensación',
    OTRO: 'Otro',
};
export const FundsProvisionStatusSchema = z.enum(['ACTIVE', 'APPLIED', 'REFUNDED']);
export const FundsProvisionStatusLabel = {
    ACTIVE: 'Activa',
    APPLIED: 'Aplicada',
    REFUNDED: 'Devuelta',
};
// ---------- Líneas ----------
const decimalString = z
    .union([z.number(), z.string()])
    .transform((v) => (typeof v === 'number' ? v.toString() : v));
export const InvoiceLineInputSchema = z.object({
    position: z.number().int().min(0).default(0),
    /** F35 — opcional: producto del catálogo del que se prellenan description/unitPriceCents/vatRate si no se especifican. */
    productId: z.string().nullable().optional(),
    /** Requerido salvo que llegue productId. */
    description: z.string().min(1).max(500).optional(),
    quantity: decimalString.default('1'),
    /** Requerido salvo que llegue productId. */
    unitPriceCents: z.number().int().optional(),
    /** Coste interno por unidad (mismo criterio que unitPriceCents). Campo interno (margen). */
    costCents: z.number().int().min(0).default(0),
    discountPct: decimalString.default('0'),
    /** Requerido salvo que llegue productId. */
    vatRate: decimalString.optional(),
    isSupplied: z.boolean().default(false),
});
export const InvoiceLineDtoSchema = z.object({
    id: z.string(),
    productId: z.string().nullable(),
    position: z.number().int(),
    description: z.string(),
    quantity: decimalString,
    unitPriceCents: z.number().int(),
    costCents: z.number().int(),
    discountPct: decimalString,
    vatRate: decimalString,
    isSupplied: z.boolean(),
    subtotalCents: z.number().int(),
    vatCents: z.number().int(),
    totalCents: z.number().int(),
});
// ---------- Series ----------
export const InvoiceSeriesDtoSchema = z.object({
    id: z.string(),
    prefix: z.string(),
    name: z.string(),
    kind: InvoiceKindSchema,
    isDefault: z.boolean(),
    active: z.boolean(),
    issuerId: z.string(),
    issuerName: z.string(),
});
export const CreateInvoiceSeriesSchema = z.object({
    prefix: z.string().min(1).max(8),
    name: z.string().min(1).max(60),
    kind: InvoiceKindSchema.default('ORDINARIA'),
    isDefault: z.boolean().default(false),
    issuerId: z.string(),
});
// ---------- Pagos ----------
export const InvoicePaymentDtoSchema = z.object({
    id: z.string(),
    paidAt: z.string().datetime(),
    amountCents: z.number().int(),
    method: PaymentMethodSchema,
    reference: z.string().nullable(),
    notes: z.string().nullable(),
});
export const CreateInvoicePaymentSchema = z.object({
    paidAt: z.string().refine((v) => !Number.isNaN(Date.parse(v)), 'Fecha no válida'),
    amountCents: z.number().int().positive(),
    method: PaymentMethodSchema.default('TRANSFERENCIA'),
    reference: z.string().max(120).nullable().optional(),
    notes: z.string().max(500).nullable().optional(),
});
// ---------- Factura ----------
export const CreateInvoiceSchema = z.object({
    seriesId: z.string(),
    clientId: z.string(),
    opportunityId: z.string().nullable().optional(),
    kind: InvoiceKindSchema.default('ORDINARIA'),
    rectifiesId: z.string().nullable().optional(),
    rectificationReason: z.string().max(500).nullable().optional(),
    dueDate: z.string().nullable().optional(),
    notes: z.string().max(2000).nullable().optional(),
    internalNotes: z.string().max(2000).nullable().optional(),
    irpfRate: decimalString.optional(),
    lines: z.array(InvoiceLineInputSchema).default([]),
});
export const UpdateInvoiceSchema = CreateInvoiceSchema.partial().extend({
    fundsApplications: z
        .array(z.object({
        provisionId: z.string(),
        amountCents: z.number().int().positive(),
    }))
        .optional(),
});
export const EmitInvoiceSchema = z.object({
    issueDate: z
        .string()
        .refine((v) => !Number.isNaN(Date.parse(v)), 'Fecha no válida')
        .optional(),
});
export const InvoiceListItemSchema = z.object({
    id: z.string(),
    seriesId: z.string(),
    seriesPrefix: z.string(),
    issuerId: z.string(),
    issuerName: z.string(),
    number: z.string().nullable(),
    year: z.number().int().nullable(),
    issueDate: z.string().datetime().nullable(),
    dueDate: z.string().datetime().nullable(),
    status: InvoiceStatusSchema,
    kind: InvoiceKindSchema,
    clientId: z.string(),
    clientDisplayName: z.string(),
    opportunityId: z.string().nullable(),
    opportunityCode: z.string().nullable(),
    subtotalCents: z.number().int(),
    vatCents: z.number().int(),
    irpfCents: z.number().int(),
    totalCents: z.number().int(),
    paidCents: z.number().int(),
    /**
     * Factura a la que rectifica, si es una rectificativa. El listado lo usa para
     * poner la rectificativa junto a la que anula, en vez de dejarlas sueltas y
     * separadas por semanas de otras facturas.
     * `.default(null)` para que un cliente viejo no rompa contra un backend nuevo.
     */
    rectifiesId: z.string().nullable().default(null),
    rectifiesNumber: z.string().nullable().default(null),
});
export const InvoiceDetailSchema = InvoiceListItemSchema.extend({
    notes: z.string().nullable(),
    internalNotes: z.string().nullable(),
    rectifiesId: z.string().nullable(),
    /**
     * Número y fecha de la factura RECTIFICADA. Son mención obligatoria de la
     * rectificativa (RD 1619/2012 art. 15.2: "los datos identificativos de la
     * factura rectificada"), así que el PDF los necesita: con `rectifiesId` solo
     * no se puede imprimir nada legible.
     */
    rectifiesNumber: z.string().nullable().optional(),
    rectifiesIssueDate: z.string().datetime().nullable().optional(),
    rectificationReason: z.string().nullable(),
    irpfRate: z.string(),
    fundsAppliedCents: z.number().int(),
    discountCents: z.number().int(),
    /** Coste interno total del documento (suma de costCents × cantidad por línea). Interno, nunca se muestra al cliente. */
    totalCostCents: z.number().int(),
    /** Margen interno = base imponible − totalCostCents. Interno. */
    marginCents: z.number().int(),
    /** Margen en % sobre la base imponible. Interno. */
    marginPct: z.number(),
    previousHash: z.string().nullable(),
    currentHash: z.string().nullable(),
    csv: z.string().nullable(),
    verifactuQrUrl: z.string().nullable(),
    emittedAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    createdByName: z.string().nullable(),
    issuerSnapshot: z.record(z.unknown()).nullable(),
    clientSnapshot: z.record(z.unknown()).nullable(),
    lines: z.array(InvoiceLineDtoSchema),
    payments: z.array(InvoicePaymentDtoSchema),
    vatBreakdown: z.array(z.object({ rate: z.number(), baseCents: z.number().int(), vatCents: z.number().int() })),
    appliedProvisions: z.array(z.object({
        provisionId: z.string(),
        amountCents: z.number().int(),
        reference: z.string().nullable(),
    })),
});
export const InvoiceListQuerySchema = z.object({
    q: z.string().optional(),
    status: InvoiceStatusSchema.optional(),
    year: z.coerce.number().int().optional(),
    clientId: z.string().optional(),
    opportunityId: z.string().optional(),
    seriesId: z.string().optional(),
    issuerId: z.string().optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(50),
});
export const InvoiceListResponseSchema = z.object({
    items: z.array(InvoiceListItemSchema),
    total: z.number().int(),
    page: z.number().int(),
    pageSize: z.number().int(),
});
// ---------- Provisiones ----------
export const FundsProvisionDtoSchema = z.object({
    id: z.string(),
    clientId: z.string(),
    clientDisplayName: z.string(),
    opportunityId: z.string().nullable(),
    opportunityCode: z.string().nullable(),
    receivedAt: z.string().datetime(),
    amountCents: z.number().int(),
    appliedCents: z.number().int(),
    refundedCents: z.number().int(),
    availableCents: z.number().int(), // derivado: amount - applied - refunded
    status: FundsProvisionStatusSchema,
    method: PaymentMethodSchema,
    description: z.string().nullable(),
    reference: z.string().nullable(),
    createdAt: z.string().datetime(),
    createdByName: z.string().nullable(),
});
export const CreateFundsProvisionSchema = z.object({
    clientId: z.string(),
    opportunityId: z.string().nullable().optional(),
    receivedAt: z.string().refine((v) => !Number.isNaN(Date.parse(v)), 'Fecha no válida'),
    amountCents: z.number().int().positive(),
    method: PaymentMethodSchema.default('TRANSFERENCIA'),
    description: z.string().max(500).nullable().optional(),
    reference: z.string().max(120).nullable().optional(),
});
export const UpdateFundsProvisionSchema = z.object({
    receivedAt: z
        .string()
        .refine((v) => !Number.isNaN(Date.parse(v)), 'Fecha no válida')
        .optional(),
    method: PaymentMethodSchema.optional(),
    description: z.string().max(500).nullable().optional(),
    reference: z.string().max(120).nullable().optional(),
});
export const RefundFundsProvisionSchema = z.object({
    amountCents: z.number().int().positive(),
    notes: z.string().max(500).nullable().optional(),
});
export const FundsProvisionListResponseSchema = z.object({
    items: z.array(FundsProvisionDtoSchema),
    /** Saldo disponible total (sum availableCents). */
    availableTotalCents: z.number().int(),
});
// ---------- F19 Emisores (multi-NIF) ----------
export const IssuerDtoSchema = z.object({
    id: z.string(),
    name: z.string(),
    legalName: z.string().nullable(),
    nif: z.string(),
    address: z.string().nullable(),
    city: z.string().nullable(),
    postalCode: z.string().nullable(),
    province: z.string().nullable(),
    country: z.string(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    bankIban: z.string().nullable(),
    bankBic: z.string().nullable(),
    invoiceFootnote: z.string().nullable(),
    active: z.boolean(),
    isDefault: z.boolean(),
});
export const CreateIssuerSchema = z.object({
    name: z.string().min(2).max(200),
    legalName: z.string().max(200).nullable().optional(),
    nif: z.string().min(1).max(20),
    address: z.string().max(300).nullable().optional(),
    city: z.string().max(100).nullable().optional(),
    postalCode: z.string().max(10).nullable().optional(),
    province: z.string().max(100).nullable().optional(),
    email: z.string().email().nullable().optional(),
    phone: z.string().max(30).nullable().optional(),
    bankIban: z.string().max(40).nullable().optional(),
    bankBic: z.string().max(15).nullable().optional(),
    invoiceFootnote: z.string().max(1000).nullable().optional(),
    isDefault: z.boolean().default(false),
});
export const UpdateIssuerSchema = CreateIssuerSchema.partial().extend({
    active: z.boolean().optional(),
});
//# sourceMappingURL=billing.js.map